import { useState, useRef, useEffect } from "react";
import { Globe, Check } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";

/**
 * Compact language switcher. Three variants:
 *   - `variant="icon"`   — pill button with globe + locale code (default, header)
 *   - `variant="text"`   — text-only pill (footer)
 *
 * Always renders a Radix-free, hand-rolled menu so the picker stays light.
 */
export default function LanguagePicker({ variant = "icon", className = "" }) {
  const { locale, setLocale, supported } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!supported || supported.length <= 1) return null;
  const active = supported.find((s) => s.code === locale) || supported[0];

  return (
    <div className={`relative inline-block ${className}`} ref={ref} data-testid="language-picker">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-white/95 px-3 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-50 transition shadow-sm"
        data-testid="language-picker-trigger"
      >
        <Globe className="w-3.5 h-3.5 text-emerald-700" />
        {variant === "text" ? active.native || active.name : active.code.toUpperCase()}
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label="Choose language"
          className="absolute right-0 mt-2 w-44 z-50 rounded-2xl bg-white border border-emerald-100 shadow-xl overflow-hidden text-sm"
          data-testid="language-picker-menu"
        >
          {supported.map((s) => {
            const isActive = s.code === locale;
            return (
              <li key={s.code}>
                <button
                  type="button"
                  onClick={() => { setLocale(s.code); setOpen(false); }}
                  className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left transition ${
                    isActive
                      ? "bg-emerald-50 text-emerald-950 font-semibold"
                      : "text-emerald-900/80 hover:bg-emerald-50/60"
                  }`}
                  data-testid={`language-option-${s.code}`}
                  aria-selected={isActive}
                  role="option"
                >
                  <span className="flex flex-col items-start leading-tight">
                    <span>{s.native || s.name}</span>
                    <span className="text-[10px] uppercase tracking-[0.16em] text-emerald-900/55 font-medium">{s.name}</span>
                  </span>
                  {isActive && <Check className="w-4 h-4 text-emerald-700" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
